import { useEffect } from 'react';
import { preloadRoute, preloadAllRoutes } from '@/config/routes';
import { useRouteMatch } from '@/utils/router';

interface RoutePreloaderProps {
  preloadAll?: boolean;
  preloadOnHover?: boolean;
  routes?: string[];
}

const RoutePreloader: React.FC<RoutePreloaderProps> = ({
  preloadAll = false,
  preloadOnHover = true,
  routes = []
}) => {
  const { pathname } = useRouteMatch();

  useEffect(() => {
    if (preloadAll) {
      // 预加载所有路由
      preloadAllRoutes();
    } else if (routes.length > 0) {
      // 预加载指定路由
      routes.forEach(route => {
        preloadRoute(route);
      });
    }
  }, [preloadAll, routes]);

  useEffect(() => {
    if (!preloadOnHover) return;

    // 预加载相邻路由
    const preloadAdjacentRoutes = () => {
      switch (pathname) {
        case '/':
          preloadRoute('/discover');
          break;
        case '/discover':
          preloadRoute('/');
          preloadRoute('/favorites');
          break;
        case '/favorites':
          preloadRoute('/discover');
          preloadRoute('/about');
          break;
        case '/about':
          preloadRoute('/favorites');
          break;
      }
    };

    // 延迟预加载，避免影响当前页面加载
    const timer = setTimeout(preloadAdjacentRoutes, 1000);
    return () => clearTimeout(timer);
  }, [pathname, preloadOnHover]);

  useEffect(() => {
    if (!preloadOnHover) return;

    // 监听链接悬停事件
    const handleLinkHover = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const link = target.closest('a[href]') as HTMLAnchorElement;
      
      if (link && link.href) {
        const url = new URL(link.href);
        if (url.origin === window.location.origin) {
          preloadRoute(url.pathname);
        }
      }
    };

    // 监听按钮悬停事件（用于程序化导航）
    const handleButtonHover = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const button = target.closest('[data-preload-route]') as HTMLElement;
      
      if (button) {
        const route = button.getAttribute('data-preload-route');
        if (route) {
          preloadRoute(route);
        }
      }
    };

    document.addEventListener('mouseover', handleLinkHover);
    document.addEventListener('mouseover', handleButtonHover);

    return () => {
      document.removeEventListener('mouseover', handleLinkHover);
      document.removeEventListener('mouseover', handleButtonHover);
    };
  }, [preloadOnHover]);

  return null; // 这是一个逻辑组件，不渲染任何内容
};

export default RoutePreloader;